import {
  Activity,
  Eye,
  Keyboard,
  MousePointer2,
  Scroll,
  Clipboard,
} from "lucide-react";

const signals = [
  { icon: Activity, label: "Tab switching" },
  { icon: Eye, label: "Idle time" },
  { icon: MousePointer2, label: "Rapid clicks" },
  { icon: Keyboard, label: "Typing rhythm" },
  { icon: Scroll, label: "Scroll speed" },
  { icon: MousePointer2, label: "Mouse movement" },
  { icon: Eye, label: "Cursor exits" },
  { icon: Clipboard, label: "Copy-paste patterns" },
  { icon: Keyboard, label: "Input gaps" },
  { icon: Scroll, label: "Reading depth" },
];

const states = [
  { label: "Focused work", tone: "#22C55E" },
  { label: "Pre-distraction", tone: "#F59E0B" },
  { label: "Wandering", tone: "#F59E0B" },
  { label: "Frozen", tone: "#F9735B" },
  { label: "Frustrated", tone: "#F9735B" },
  { label: "Passive consumption", tone: "#7C3AED" },
  { label: "Creative flow", tone: "#22C55E" },
  { label: "Hyperfocus risk", tone: "#7C3AED" },
];

export default function Signals() {
  return (
    <section
      data-testid="signals-section"
      className="px-6 lg:px-10 py-24 lg:py-32 bg-[#F1F5F9]/50"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5 fade-up">
          <p className="text-sm tracking-[0.18em] uppercase font-semibold text-[#475569]">
            How it understands you
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.05]">
            Mindrail watches for the signals you usually notice too late.
          </h2>
          <p className="mt-6 text-lg text-[#475569] leading-relaxed">
            It reads live behavioural patterns from your browser and quietly
            classifies what kind of moment you&apos;re in. Then it gives the
            smallest useful support.
          </p>
          <div className="mt-7 space-y-3 text-[#0F172A]">
            <p className="flex items-baseline gap-3">
              <span className="font-mono text-xs text-[#F59E0B]">—</span>
              Not a lecture.
            </p>
            <p className="flex items-baseline gap-3">
              <span className="font-mono text-xs text-[#F59E0B]">—</span>
              Not a guilt trip.
            </p>
            <p className="flex items-baseline gap-3">
              <span className="font-mono text-xs text-[#22C55E]">—</span>
              <span className="font-display text-lg">A next step.</span>
            </p>
          </div>
        </div>

        <div className="lg:col-span-7 fade-up">
          <div className="bg-white border border-[#E2E8F0] rounded-3xl p-6 lg:p-8 shadow-[0_8px_30px_rgba(42,48,43,0.04)]">
            <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#475569]">
              Live signals
            </p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {signals.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]"
                >
                  <s.icon
                    className="w-3.5 h-3.5 text-[#22C55E] shrink-0"
                    strokeWidth={1.5}
                  />
                  <span className="text-sm text-[#0F172A] truncate">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-7 pt-6 border-t border-dashed border-[#E2E8F0]">
              <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#475569]">
                Detected states
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {states.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm border bg-[#F8FAFC]"
                    style={{
                      borderColor: s.tone + "55",
                      color: "#0F172A",
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: s.tone }}
                    />
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
